import { formatLatLng } from "@spoferan/spoferan-ts-core";
import { degreesToRadians, hav, havDistance, havFromSin, sinDeltaBearing, sinFromHav, sinSumFromHav } from "@spoferan/spoferan-ts-core";
import { NativeApp } from "./NativeApp.mjs";
export class GeolocationService {
  /**
   * The mean radius of the earth in meters.
   */
  static EARTH_RADIUS = 6371009;
  /**
   * Retrieves the geolocation of the user.
   */
  static async locate(successCallback, errorCallback = null) {
    if (NativeApp.isNativeApp()) {
      await NativeApp.locate({ maxSecondsOld: 60 * 5 }, successCallback);
    } else {
      this.locateViaBrowser(successCallback, errorCallback);
    }
  }
  static locateViaBrowser(successCallback, errorCallback = null) {
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        successCallback(formatLatLng([position.coords.latitude, position.coords.longitude]));
      }, errorCallback, {
        maximumAge: 5 * 60 * 1e3
        // refresh geolocation every 5 minutes
      });
    } else if (errorCallback) {
      errorCallback();
    }
  }
  /**
   * Gets the distance in meters between the specified set of coordinates.
   *
   * @param latLngs
   * @returns {number}
   */
  static getDistanceOfSet(latLngs) {
    let distance = 0;
    if (latLngs && latLngs.length) {
      for (let i = 0; i < latLngs.length - 1; i++) {
        distance += this.getDistanceBetween(latLngs[i], latLngs[i + 1]);
      }
    }
    return distance;
  }
  /**
   * Gets the distance in meters between the two specified coordinates.
   *
   * @param latLngFrom
   * @param latLngTo
   * @returns {number}
   */
  static getDistanceBetween(latLngFrom, latLngTo) {
    latLngFrom = formatLatLng(latLngFrom);
    latLngTo = formatLatLng(latLngTo);
    const latFrom = degreesToRadians(latLngFrom.lat);
    const lngFrom = degreesToRadians(latLngFrom.lng);
    const latTo = degreesToRadians(latLngTo.lat);
    const lngTo = degreesToRadians(latLngTo.lng);
    const latDelta = latTo - latFrom;
    const lngDelta = lngTo - lngFrom;
    const angle = 2 * Math.asin(Math.sqrt(Math.sin(latDelta / 2) ** 2 + Math.cos(latFrom) * Math.cos(latTo) * Math.sin(lngDelta / 2) ** 2));
    return Math.round(this.EARTH_RADIUS * angle);
  }
  /**
   * Checks whether the pointLatLng is on the line between pathStartLatLng and pathEndLatLng within the given tolerance in meters.
   *
   * @param pointLatLng
   * @param pathStartLatLng
   * @param pathEndLatLng
   * @param toleranceInMeters
   * @returns {boolean}
   */
  static isPointOnPath(pointLatLng, pathStartLatLng, pathEndLatLng, toleranceInMeters = 1.1) {
    const tolerance = toleranceInMeters / this.EARTH_RADIUS;
    const havTolerance = hav(tolerance);
    const pointRadiansLat = degreesToRadians(pointLatLng.lat);
    const pointRadiansLng = degreesToRadians(pointLatLng.lng);
    const pathStartRadiansLat = degreesToRadians(pathStartLatLng.lat);
    const pathStartRadiansLng = degreesToRadians(pathStartLatLng.lng);
    const pathEndRadiansLat = degreesToRadians(pathEndLatLng.lat);
    const pathEndRadiansLng = degreesToRadians(pathEndLatLng.lng);
    const pointHavDistanceFromStart = havDistance(pathStartRadiansLat, pathStartRadiansLng, pointRadiansLat, pointRadiansLng);
    if (pointHavDistanceFromStart <= havTolerance) {
      return true;
    }
    const pointHavDistanceFromEnd = havDistance(pathEndRadiansLat, pathEndRadiansLng, pointRadiansLat, pointRadiansLng);
    if (pointHavDistanceFromEnd <= havTolerance) {
      return true;
    }
    const sinBearing = sinDeltaBearing(pathStartRadiansLat, pathStartRadiansLng, pathEndRadiansLat, pathEndRadiansLng, pointRadiansLat, pointRadiansLng);
    const sinDist13 = sinFromHav(pointHavDistanceFromStart);
    const havCrossTrack = havFromSin(sinDist13 * sinBearing);
    if (havCrossTrack > havTolerance) {
      return false;
    }
    const pathDistance = havDistance(pathStartRadiansLat, pathStartRadiansLng, pathEndRadiansLat, pathEndRadiansLng);
    const term = pathDistance + havCrossTrack * (1 - 2 * pathDistance);
    if (pointHavDistanceFromStart > term || pointHavDistanceFromEnd > term) {
      return false;
    }
    if (pathDistance < 0.74) {
      return true;
    }
    const cosCrossTrack = 1 - 2 * havCrossTrack;
    const havAlongTrack13 = (pointHavDistanceFromStart - havCrossTrack) / cosCrossTrack;
    const havAlongTrack23 = (pointHavDistanceFromEnd - havCrossTrack) / cosCrossTrack;
    const sinSumAlongTrack = sinSumFromHav(havAlongTrack13, havAlongTrack23);
    return sinSumAlongTrack > 0;
  }
  /**
   * Finds the index of the given lat lng in the set of the given route lat lngs.
   *
   * @param {object} latLng
   * @param {array} route
   * @param {number} toleranceInMeters
   * @returns {number}
   */
  static findLatLngIndexOnRoute(latLng, route, toleranceInMeters = 1.1) {
    let index = -1;
    for (let i = 0; i < route.length - 1; i++) {
      if (GeolocationService.isPointOnPath(latLng, route[i], route[i + 1], toleranceInMeters)) {
        index = i;
        break;
      }
    }
    return index;
  }
  /**
   * Checks whether the distance of the specified lat lngs is below the given distance, e.g., they are assumed to be the same.
   *
   * @param latLng1
   * @param latLng2
   * @param accuracyInMeters
   * @returns {boolean}
   */
  static isSame(latLng1, latLng2, accuracyInMeters = 1.1) {
    return this.getDistanceBetween(latLng1, latLng2) < accuracyInMeters;
  }
  /**
   * Gets the elevation data of the specified coordinates.
   *
   * @param latLngs
   * @returns {{}}
   */
  static getElevationData(latLngs) {
    let dp = 0, dm = 0;
    for (let i = 0; i < latLngs.length - 1; i++) {
      let diff = parseFloat(latLngs[i + 1].ele) - parseFloat(latLngs[i].ele);
      if (diff < 0) {
        dm += diff;
      } else if (diff > 0) {
        dp += diff;
      }
    }
    const elevation = [];
    let sum = 0;
    for (let i = 0, len = latLngs.length; i < len; i++) {
      let ele = parseFloat(latLngs[i].ele);
      elevation.push(ele);
      sum += ele;
    }
    return {
      max: Math.max.apply(null, elevation),
      min: Math.min.apply(null, elevation),
      pos: Math.abs(dp),
      neg: Math.abs(dm),
      avg: sum / elevation.length
    };
  }
  /**
   * Splits the given route lat lngs to ensure every lat lng has a max distance of the given accuracy to the next lat lng.
   *
   * @param {object[]} latLngs
   * @param {number} accuracy
   * @returns {object[]}
   */
  static sharpenRouteAccuracy(latLngs, accuracy) {
    let i, len = latLngs.length, sharpLatLngs = [];
    for (i = 1; i < len; i++) {
      let cur = latLngs[i - 1], next = latLngs[i], dist = GeolocationService.getDistanceBetween(cur, next), factor = accuracy / dist, dLat = factor * (next.lat - cur.lat), dLng = factor * (next.lng - cur.lng);
      if (dist > accuracy) {
        while (dist > accuracy) {
          cur = { lat: cur.lat + dLat, lng: cur.lng + dLng };
          dist = GeolocationService.getDistanceBetween(cur, next);
          sharpLatLngs.push(cur);
        }
      } else {
        sharpLatLngs.push(cur);
      }
    }
    sharpLatLngs.push(latLngs[len - 1]);
    return sharpLatLngs;
  }
}
